import { useCallback, useMemo } from 'react'
import { SearchItemLabel, usePopup } from '.'
import { Chip } from '@mui/material'
import { styled } from '@mui/system'
import {
  TextMatchCondition,
  TextMatchMode,
} from '../../../../domain/value-object/WbsItemSearchConditionV2Vo'
import { colorPalette } from '../../../style/colorPalette'

type Props = {
  header: string
  value: TextMatchCondition[] | undefined
  isLoading?: boolean
  onChange: (value: TextMatchCondition[] | undefined) => void
  onClose?: () => void
  search: () => void
}

const chipColor: Record<
  TextMatchMode,
  { background: string; color: string; border: string }
> = {
  include: {
    background: colorPalette.skyBlue[1],
    color: colorPalette.skyBlue[8],
    border: colorPalette.skyBlue[7],
  },
  exclude: {
    background: colorPalette.pink[0],
    color: colorPalette.pink[6],
    border: colorPalette.pink[5],
  },
}

const ChipRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
})

export const TextMatchConditionItemLabel = ({
  header,
  value,
  isLoading,
  onChange,
  onClose,
  search,
}: Props) => {
  const { ref, open, openPopup, closePopup } = usePopup()

  const onClear = useCallback(() => {
    onChange(undefined)
    onClose?.()
  }, [onChange, onClose])

  const onSearch = useCallback(() => {
    closePopup()
    search()
  }, [closePopup, search])

  const displayText = useMemo(() => {
    if (!value || value.length === 0) return ''
    return value.map(c => c.value).join(', ')
  }, [value])

  if (!value || value.length === 0) return <></>
  return (
    <>
      <SearchItemLabel
        ref={ref}
        active={open}
        isLoading={isLoading}
        onClick={openPopup}
        onClear={onClear}
      >
        <ChipRow>
          {header}:
          {value.map((c, i) => {
            const style = chipColor[c.mode]
            return (
              <Chip
                key={i}
                label={c.value}
                size="small"
                sx={{
                  height: '20px',
                  backgroundColor: style.background,
                  color: style.color,
                  border: `1px solid ${style.border}`,
                  textDecoration:
                    c.mode === 'exclude' ? 'line-through' : 'none',
                  '& .MuiChip-label': { padding: '0 6px' },
                }}
              />
            )
          })}
        </ChipRow>
      </SearchItemLabel>
    </>
  )
}
